import { useState, useEffect, useRef } from "react";
import axios from "../../../lib/axios";
import {
  ADMIN_ORDER_FIND_MANY,
  ADMIN_ORDER_UPDATE_STATUS,
  ORDER_ADMIN_FIND_ONE,
} from "../../../lib/endpoints/order";
import { DEFAULT_ROWS_PER_PAGE } from "../../../config-global";
import { OrderStatusEnum } from "../../../enums/orderStatus";
import {
  convertOrderStatusCodeToOrderStatusEnum,
  convertOrderStatusEnumToStatusCode,
} from "../../../utils/convertOrderStatusValue";
import { OrderModel } from "../../../models/order";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { MerchantUserModel } from "../../../models/merchantUser";
import {
  closeSound,
  removeNewOrderId,
} from "../../../store/notification_slice";
import { ShipmentMethodEnum } from "../../../enums/shipmentMethod";
import { useTranslation } from "react-i18next";
// ---------------------------------------------------

const useOrderController = () => {
  // ----------------- variables ------------------
  const authData: MerchantUserModel =
    useSelector((state: RootState) => state?.auth?.data) || null!;
  const [data, setData] = useState<OrderModel[]>([]);
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingGetData, setLoadingGetData] = useState<boolean>(false);
  const [loadingFetchDataMore, setLoadingFetchDataMore] =
    useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null!);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedId, setSelectedId] = useState<string>(null!);
  const [selectedData, setSelectedData] = useState<OrderModel>(null!);
  const [currentOrderStatus, setCurrentOrderStatus] = useState<OrderStatusEnum>(
    OrderStatusEnum.CURRENT_ORDER
  );
  const [openFormImportRider, setOpenFormImportRider] =
    useState<boolean>(false);
  const [openConfirmRiderAssistant, setOpenConfirmRiderAssistant] =
    useState<boolean>(false);
  const [loadingRiderAssistant, setLoadingRiderAssistant] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const InputRiderDetail = useRef<HTMLInputElement>(null!);

  const notification = useSelector((state: RootState) => state?.notification);
  const { t } = useTranslation("global");

  // ----------------- functions --------------------

  const handleGetData = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
          OrderStatusEnum.CURRENT_ORDER
        )}`,
        {
          filter: {
            merchantId: authData?.merchantId,
          },
        }
      );
      setData(res?.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleGetDataByOrderStatus = async (
    getOrderStatus: OrderStatusEnum
  ) => {
    try {
      setLoadingGetData(true);
      const passOrderStatus: OrderStatusEnum =
        getOrderStatus === OrderStatusEnum.Rider_Getting_Order ||
        getOrderStatus === OrderStatusEnum.READY_TO_PICKUP
          ? OrderStatusEnum.To_Ship
          : getOrderStatus;
      const res = await axios.post(
        `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
          passOrderStatus
        )}`,
        {
          filter: {
            merchantId: authData?.merchantId,
          },
        }
      );

      if (getOrderStatus === OrderStatusEnum.Rider_Getting_Order) {
        setData(
          res?.data?.filter(
            (item: OrderModel) =>
              item?.rider && item?.orderMethod === ShipmentMethodEnum.DELIVERY
          )
        );
      } else if (getOrderStatus === OrderStatusEnum.To_Ship) {
        setData(
          res?.data?.filter(
            (item: OrderModel) =>
              !item?.rider && item?.orderMethod === ShipmentMethodEnum.DELIVERY
          )
        );
      } else if (getOrderStatus === OrderStatusEnum.READY_TO_PICKUP) {
        setData(
          res?.data?.filter(
            (item: OrderModel) =>
              item?.orderMethod === ShipmentMethodEnum.PICK_UP
          )
        );
      } else {
        setData(res?.data);
      }
    } catch (err) {
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleRefetchData = async () => {
    try {
      // const res = await axios.post(
      //   `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
      //     currentOrderStatus
      //   )}`,
      //   {
      //     filter: {
      //       merchantId: authData?.merchantId,
      //     },
      //   }
      // );
      // setData(res?.data);
      handleGetDataByOrderStatus(currentOrderStatus);
    } catch (err) {
    } finally {
    }
  };

  const handleGetDataByOrderId = async () => {
    try {
      setLoadingGetData(true);
      const res = await axios.post(ADMIN_ORDER_FIND_MANY, {
        filter: {
          merchantId: authData?.merchantId,
          orderNo: searchRef.current.value,
        },
      });
      const newData: OrderModel[] = res?.data;
      if (newData?.length > 0) {
        setCurrentOrderStatus(
          convertOrderStatusCodeToOrderStatusEnum(
            newData[0]?.status?.code
          ) as OrderStatusEnum
        );
      }
      setData(newData);
    } catch (err) {
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleAssignOrderToRiderAssistant = async () => {
    try {
      setErrorMessage("");
      setOpenFormImportRider(false);
      setLoadingRiderAssistant(true);
      if (!InputRiderDetail?.current?.value) {
        console.log("Please Input Rider assistant name");
        return; // Exit the function
      }
      const res = await axios.post(
        `${ADMIN_ORDER_UPDATE_STATUS}?orderId=${selectedId}&status=${6}&riderAssist=${true}&riderDetail=${
          InputRiderDetail?.current?.value
        }`
      );
      console.log(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRiderAssistant(false);
      setOpenFormImportRider(false);
      setOpenConfirmRiderAssistant(false);
      handleRefetchData();
    }
  };

  const handleOpenFormImportRider = (getId: string) => {
    setOpenFormImportRider(true);
    setSelectedId(getId);
  };

  const handleOpenConfirmFormRiderAssistant = () => {
    if (!InputRiderDetail?.current?.value) {
      setErrorMessage(`${t("order_page.Please Input rider assistant")}`);
      return false;
    } else {
      setOpenConfirmRiderAssistant(true);
      setErrorMessage("");
    }
  };

  const handleCurrentOrderStatusChange = (newValue: OrderStatusEnum) => {
    if (newValue) {
      setCurrentOrderStatus(newValue);
      handleGetDataByOrderStatus(newValue);
      setCurrentPage(1);
    }
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
    // setSelectedData(data?.filter((item: any) => item?.id === getId)[0]);
  };

  const handleFetchDataMore = () => {};

  const handlePageChange = (event: any, newPage: any) => {
    if (newPage === countPagination) {
      if (!loadingFetchDataMore) {
        handleFetchDataMore();
      }
    }
    setCurrentPage(newPage);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleOpenForm = () => {
    setOpenMenu(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedData(null!);
  };

  const handleClickViewData = () => {
    setOpenForm(true);
    setSelectedData(data?.filter((item: any) => item?.id === selectedId)[0]);
    setOpenMenu(null);
    dispatch(removeNewOrderId(selectedId));
    dispatch(closeSound());
  };

  // const handleGetDataById = async (getId: string) => {
  //   try {
  //     const res = await axios.get(`${ORDER_ADMIN_FIND_ONE}?id=${getId}`);
  //     if (res?.data?.merchantId === authData?.merchantId) {
  //       setData([res?.data, ...data]);
  //     }
  //     console.log(res);
  //   } catch (err) {}
  // };

  // ----------------- useEffect --------------------
  const rows = data.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );
  // const rows=data;
  const countPagination = Math.ceil(data.length / DEFAULT_ROWS_PER_PAGE);
  //   const isSearchNotFound: boolean = !data.length;

  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      notification?.data &&
      notification?.data?.status?.code ===
        convertOrderStatusEnumToStatusCode(currentOrderStatus)
    ) {
      setData([notification?.data, ...data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.data]);

  return {
    data,
    loading,
    rows,
    countPagination,
    openForm,
    searchRef,
    handleGetDataByOrderId,
    currentPage,
    loadingFetchDataMore,
    handleOpenMenu,
    handlePageChange,
    openMenu,
    handleCloseMenu,
    handleOpenForm,
    handleCloseForm,
    handleClickViewData,
    selectedData,
    currentOrderStatus,
    handleCurrentOrderStatusChange,
    loadingGetData,
    handleRefetchData,
    handleOpenFormImportRider,
    handleCloseFormImportRider: () => setOpenFormImportRider(false),
    openFormImportRider,
    handleAssignOrderToRiderAssistant,
    InputRiderDetail,
    openConfirmRiderAssistant,
    handleOpenConfirmFormRiderAssistant,
    handleCloseConfirmFormRiderAssistant: () =>
      setOpenConfirmRiderAssistant(false),
    loadingRiderAssistant,
    errorMessage,
    // isSearchNotFound,
  };
};
export default useOrderController;
